import { useEffect, useRef, useState } from "react";
import { CustomerDatabaseStore } from "../../../../store/main/CustomerDatabaseStore";
import { Box, Typography } from "@mui/material";
import ReportFiltersComponent from "../ReportFiltersComponent";
import ReportScreen from "../../../../common/enums/ReportScreens";
import { ReportFilters } from "../../../../domain/models/report-filters";
import ReportFiltersLoading from "../ReportFiltersLoading";
import Roles from "../../../../common/enums/Roles";
import BrickManagementReportExpandableTable, {
  BrickWorkingRow,
} from "./BrickManagementReportExpandableTable";
import { BrickWorkingReportsStore } from "../../../../store/main/BrickWorkingReportsStore";
import {
  BrickWorkingReportData,
  MonthlyVisits,
} from "../../../../domain/models/brick-working-report/fetch-brick-working-report";
import BrickWorkingReportTab from "./BrickWorkingReportTab";
import moment from "moment";

type BrickWorkingReportParams = {
  year: number;
  zone?: string;
  region?: string;
  customer_type_id?: string;
  product_ids?: string[];
  quarter?: string;
};

type ColumnFilterKey = "area" | "headquarter" | "brick";

type ColumnFilters = {
  area: string[];
  headquarter: string[];
  brick: string[];
};

const BrickWorkingReportDesktop = () => {
  const currentDate = new Date();
  const defaultYear = currentDate.getFullYear();
  const { decodeToken, userDetails, userRole } = CustomerDatabaseStore();
  const { fetchBrickWorkingReportResponse, FetchBrickWorkingReportState } =
    BrickWorkingReportsStore();
  const [year, setYear] = useState<number>(defaultYear);
  const [loading, setLoading] = useState<boolean>(false);
  const [brickWorkingData, setBrickWorkingData] = useState<
    BrickWorkingReportData[]
  >([]);
  const [rows, setRows] = useState<BrickWorkingRow[]>([]);
  const isHierarchyReady = useRef(false);
  const lastFetchKey = useRef<string>("");
  const [reportParams, setReportParams] = useState<BrickWorkingReportParams>({
    year: defaultYear,
  });
  const [allRows, setAllRows] = useState<BrickWorkingRow[]>([]);

  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({
    area: [],
    headquarter: [],
    brick: [],
  });

  const [resetHierarchyKey, setResetHierarchyKey] = useState(0);

  const handleHierarchyChange = (zone?: string, region?: string) => {
    setReportParams((prev) => ({
      ...prev,
      zone,
      region,
    }));
  };

  useEffect(() => {
    if (!isHierarchyReady.current) return;
    if (
      (userRole.role === Roles.COUTNRY_MANAGER ||
        userRole.role === Roles.ZONE_MANAGER ||
        userRole.role === Roles.REGION_MANAGER) &&
      !reportParams.zone
    ) {
      return;
    }

    const fetchKey = JSON.stringify({
      year: reportParams.year,
      zone: reportParams.zone,
      region: reportParams.region,
      customer_type_id: reportParams.customer_type_id,
      product_ids: reportParams.product_ids,
      // quarter: "Q1",
    });
    if (lastFetchKey.current === fetchKey) return;

    lastFetchKey.current = fetchKey;

    fetchBrickWorkingData(reportParams);
  }, [reportParams, userRole.role]);

  useEffect(() => {
    if (!userRole.role || !userDetails) return;
    if (isHierarchyReady.current) return;

    if (userRole.role === Roles.COUTNRY_MANAGER) {
      setReportParams({
        year: defaultYear,
        zone: userDetails.hq_zone_id?.toString(),
        region: userDetails.hq_region_id?.toString(),
      });
    }

    if (userRole.role === Roles.ZONE_MANAGER) {
      setReportParams({
        year: defaultYear,
        zone: userDetails.hq_zone_id?.toString(),
      });
    }

    if (userRole.role === Roles.REGION_MANAGER) {
      setReportParams({
        year: defaultYear,
        zone: userDetails.hq_zone_id?.toString(),
        region: userDetails.hq_region_id?.toString(),
      });
    }

    if (userRole.role === Roles.AREA_MANAGER) {
      setReportParams({
        year: defaultYear,
      });
    }

    isHierarchyReady.current = true;
  }, [userRole.role, userDetails]);

  useEffect(() => {
    decodeToken();
  }, []);

  const fetchBrickWorkingData = async (params: BrickWorkingReportParams) => {
    setLoading(true);
    try {
      const response = await fetchBrickWorkingReportResponse({
        year: params.year,
        zone: params.zone,
        region: params.region,
        customer_type_id: params.customer_type_id,
        product_ids: params.product_ids,
        // quarter: "Q1",
      });

      if (response.success && response) {
        setBrickWorkingData(response.data);
        setRows(mapApiToTableRows(response.data));
        const mappedRows = mapApiToTableRows(response.data);
        setAllRows(mappedRows);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const applyColumnFilters = (
    rows: BrickWorkingRow[],
    filters: ColumnFilters
  ) => {
    return rows.filter((row) => {
      return (
        (filters.area.length === 0 || filters.area.includes(row.area)) &&
        (filters.headquarter.length === 0 ||
          filters.headquarter.includes(row.headquarter)) &&
        (filters.brick.length === 0 || filters.brick.includes(row.brick))
      );
    });
  };

const handleApplyFilter = (key: ColumnFilterKey, values: string[]) => {
  setColumnFilters((prev) => {
    const allValues = Array.from(
      new Set(allRows.map((r) => r[key]))
    );

    // 🟢 Select All = remove filter
    if (values.length === 0 || values.length === allValues.length) {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    }

    return {
      ...prev,
      [key]: values,
    };
  });
};



  const quarterToKey = (q: string): "q1" | "q2" | "q3" | "q4" => {
    switch (q) {
      case "Q1":
        return "q1";
      case "Q2":
        return "q2";
      case "Q3":
        return "q3";
      case "Q4":
        return "q4";
      default:
        throw new Error("Invalid quarter");
    }
  };

  const mapApiToTableRows = (
    data: BrickWorkingReportData[]
  ): BrickWorkingRow[] => {
    return data.map((item) => {
      const row: BrickWorkingRow = {
        area: item.area,
        headquarter: item.headquarter,
        brick: item.brick,
        brickPotential: item.brick_potential ?? 0,

        q1: { totalVisits: 0, acps: 0, secondaryOrders: 0, invoices: 0 },
        q2: { totalVisits: 0, acps: 0, secondaryOrders: 0, invoices: 0 },
        q3: { totalVisits: 0, acps: 0, secondaryOrders: 0, invoices: 0 },
        q4: { totalVisits: 0, acps: 0, secondaryOrders: 0, invoices: 0 },
      };

      item.quarters?.forEach((q) => {
        const key = quarterToKey(q.quarter);

        // ✅ month order fixed per quarter
        const monthOrderMap: Record<string, string[]> = {
          Q1: ["Jan", "Feb", "Mar"],
          Q2: ["Apr", "May", "Jun"],
          Q3: ["Jul", "Aug", "Sep"],
          Q4: ["Oct", "Nov", "Dec"],
        };

        const months = monthOrderMap[q.quarter].map(
          (m) => q.monthly_visits?.[m]?.visit_days?.split("|").map(Number) ?? []
        );

        row[key] = {
          totalVisits: q.total_visits ?? 0,
          acps: q.acp_count ?? 0,
          secondaryOrders: q.order_amount ?? 0,

          // ✅ invoices mapped correctly
          invoices: q.invoice_count ?? 0,

          months,
        };
      });

      return row;
    });
  };

  const filterData = (filter: ReportFilters) => {
    const selectedYear = parseInt(
      moment(filter.date?.toString()).format("YYYY")
    );

    setYear(selectedYear);
     lastFetchKey.current = "";

    setReportParams((prev) => {
      const baseParams: BrickWorkingReportParams = {
        ...prev, // keeps hierarchy
        year: selectedYear,
        customer_type_id: filter.customerType || undefined,
        product_ids: filter.products || undefined,
      };

      // 👇 ROLE-BASED OVERRIDE
      if (userRole.role === Roles.COUTNRY_MANAGER) {
        return {
          ...baseParams,
          zone: prev.zone,
          region: prev.region,
        };
      }

      if (userRole.role === Roles.ZONE_MANAGER) {
        return {
          ...baseParams,
          zone: prev.zone,
          region: undefined,
        };
      }

      if (userRole.role === Roles.REGION_MANAGER) {
        return {
          ...baseParams,
          zone: prev.zone,
          region: prev.region,
        };
      }

      // AREA MANAGER
      return {
        year: selectedYear,
        customer_type_id: filter.customerType || undefined,
        product_ids: filter.products || undefined,
      };
    });
  };

  const clearData = () => {
    const currentYear = new Date().getFullYear();
    setYear(currentYear);

    setColumnFilters({
    area: [],
    headquarter: [],
    brick: [],
  });

    setReportParams((prev) => {
      return {
        year: currentYear,
        zone: prev.zone,
        region: prev.region,
      };
    });
    setResetHierarchyKey((prev) => prev + 1);
  };

  useEffect(() => {
  setColumnFilters({
    area: [],
    headquarter: [],
    brick: [],
  });
}, [allRows]);


  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        width="100%"
        mb={2}
      >
        {/* LEFT: Heading */}
        <Typography variant="h5" fontWeight={550} sx={{ whiteSpace: "nowrap" }}>
          Brick Working Report
        </Typography>

        {/* RIGHT: Filters */}
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          flexGrow={1}
          maxWidth="100%"
          mt={1}
        >
          {Object.keys(userDetails).length > 0 ? (
            <ReportFiltersComponent
              type={ReportScreen.BRICK_WORKING_REPORT}
              successCallback={filterData}
              clearSuccessCallBack={clearData}
              userRole={userRole.role}
              userDetails={userDetails}
              gridColumns={20}
              justifyContent="end"
            />
          ) : (
            <ReportFiltersLoading />
          )}
        </Box>
        {(userRole.role === Roles.COUTNRY_MANAGER ||
          userRole.role === Roles.ZONE_MANAGER) && (
          <BrickWorkingReportTab
            key={resetHierarchyKey}
            userDetails={userDetails}
            year={year}
            onHierarchyChange={handleHierarchyChange}
          />
        )}
      </Box>
      <BrickManagementReportExpandableTable
        rows={rows}
        allRows={allRows}  
        loading={loading}
        onApplyFilter={handleApplyFilter}
        columnFilters={columnFilters} 
      />
    </>
  );
};

export default BrickWorkingReportDesktop;
